import { SxProps, Theme, useMediaQuery } from '@mui/material'
import Backdrop from '@mui/material/Backdrop'
import Grid from '@mui/material/Grid2'
import dayjs from 'dayjs'
import { has, includes, isUndefined } from 'lodash'
import { FC, PropsWithChildren, useEffect, useState } from 'react'
import { useCookies } from 'react-cookie'
import { useLocation } from 'react-router-dom'
import { OIDC_SESSION_STORAGE } from '../../api/endpoints.constants'
import { useFoUserStorageInfoApiV1UserFolderFileObjectStorageInfoGet } from '../../api/gen/fileGW'
import { useInvalidateWorkplaceListQuery } from '../../api/hooks/useInvalidateWorkplaceListQuery'
import { NOTIFICATIONS_ROUTE } from '../../constants/routing.constants'
import { BalanceZeroDialog } from '../../dialogs/BalanceZeroDialog'
import { CookiesDialog } from '../../dialogs/CookiesDialog'
import { DemoRegistrationDialog } from '../../dialogs/DemoRegistrationDialog'
import { LoaderDialog } from '../../dialogs/LoaderDialog'
import { MeetCreateDialog } from '../../dialogs/MeetCreateDialog'
import { OnboardingStep } from '../../dialogs/Onboarding/OnboardingStep'
import { PackageSubscriptionUpdateDialog } from '../../dialogs/PackageSubscriptionUpdateDialog'
import { RegularRequestsDialog } from '../../dialogs/RegularRequestsDialog'
import { useLocalStorage } from '../../hooks/useLocalStorage'
import { useSessionStorage } from '../../hooks/useSessionStorage'
import AssistantShopSidebar from '../../pages/InternalPage/AssistantShopPage/AssistantShopSidebar'
import ProjectSettingsSidebar from '../../pages/InternalPage/ProjectPage/components/ProjectSettingsSidebar'
import { RecordsSetting } from '../../pages/InternalPage/RecordsPage/components/RecordsSetting'
import CitationSideBar from '../../pages/InternalPage/RequestMessagesPage/components/CitationSideBar'
import { RequestsSetting } from '../../pages/InternalPage/RequestsPage/components/RequestsSetting'
import { useNotificationsStore } from '../../store/storeNotifications'
import { useProfileStore } from '../../store/storeProfile'
import { useRequestsStore } from '../../store/storeRequests'
import { useUserStore } from '../../store/storeUser'
import { useWrapperStore } from '../../store/storeWrapper'
import { theme } from '../../theme'
import { SidebarType } from '../../types/sidebars'
import { HeaderApp } from './HeaderApp/HeaderApp'
import { NavigationSidebar } from './NavigationSidebar'
import { NotificationsSettingsSidebar } from './NotificationsSettingsSidebar/NotificationsSettingsSidebar'
import { NotificationsSidebar } from './NotificationsSidebar/NotificationsSidebar'
import { WrapperNotification } from './WrapperNotification'

type Props = PropsWithChildren<{
    styleChildren?: SxProps<Theme>
}>

export const Wrapper: FC<Props> = ({ children, styleChildren }) => {
    const { pathname } = useLocation()
    const [openBalanceDialog, setOpenBalanceDialog] = useState<boolean>(false)
    const [openPromoDialog, setOpenPromoDialog] = useState<boolean>(false)
    const [cookies, setCookie] = useCookies([
        'isShowBalanceDialogByUserBalance',
        'isShowBalanceZeroDialog',
    ])
    const [user] = useSessionStorage(OIDC_SESSION_STORAGE, null)
    const { balance, userTariff, fetchUserTariff } = useProfileStore((state) => state)
    const [openDemoRegistrationDialog, setOpenDemoRegistrationDialog] = useState<boolean>(false)
    const [openCookiesDialog, setOpenCookiesDialog] = useState<boolean>(false)
    const [openShowBalanceZeroDialog, setOpenShowBalanceZeroDialog] = useState<boolean>(false)

    const invalidateWorkplaceListQuery = useInvalidateWorkplaceListQuery()

    const {
        modelListActive,
        fetchModelList,
        fetchProjectList,
        projectListActive,
        isLoadingCreateProject,
        createProject,
        errorModelList,
        setStateNotification,
        resetModelDetail,
        isOpenLoader,
        isNoneUserTariff,
        isTempUser,
        setIsTempUser,
        fetchAssistantFiltersList,
        setOpenDialogAddBotToMeetNow,
        setOpenDialogAddBotToMeet,
        setOpenDialogTranscriptVideo,
        isOpenDialogAddBotToMeetNow,
        isOpenDialogAddBotToMeet,
        hideHeader,
        isLoadingProjectList,
        openRegularRequestsDialogThreadId,
        openSidebarType,
        stepOnboarding,
        setStepOnboarding,
    } = useWrapperStore((state) => state)
    const { data: storageData } = useFoUserStorageInfoApiV1UserFolderFileObjectStorageInfoGet()
    const [timeLeft, setTimeLeft] = useState(3)
    const { numberOfUnreadNotifications } = useNotificationsStore((state) => state)

    const {
        clearRequest,
        clearRequestSetting,
        isDoneUpdateRequest,
        errorUpdateRequest,
        setIsDoneUpdateRequest,
        isShowRegistrationDialog,
        setIsShowRegistrationDialog,
    } = useRequestsStore((state) => state)
    const { getUserPersonalData, userPersonalData } = useUserStore((state) => state)

    const isMobile = useMediaQuery(theme.breakpoints.down('md'))

    const [isShowBalanceDialogByFirstTime, setIsShowBalanceDialogStorage] = useLocalStorage(
        'isShowBalanceDialogByFirstTime',
        null,
    )

    const [isShowPromoDialogByFirstTime, setIsShowPromoDialogStorage] = useLocalStorage(
        'isShowPromoDialogByFirstTime',
        null,
    )

    const [isShowCookiesDialog, setIsShowCookiesDialog] = useLocalStorage(
        'isShowCookiesDialog',
        null,
    )

    useEffect(() => {
        if (!projectListActive) {
            fetchProjectList()
        }
    }, [projectListActive])

    useEffect(() => {
        if (!modelListActive) {
            fetchModelList({ show_restricted: true })
        }
    }, [modelListActive])

    useEffect(() => {
        fetchAssistantFiltersList()
    }, [])

    useEffect(() => {
        getUserPersonalData()
        fetchUserTariff()
    }, [])

    useEffect(() => {
        if (
            !isLoadingCreateProject &&
            !isLoadingProjectList &&
            projectListActive &&
            projectListActive.length === 0
        ) {
            createProject({ title: 'Новый проект 1' })
        }

        invalidateWorkplaceListQuery()
    }, [projectListActive, isLoadingProjectList])

    const handleCloseBalanceDialog = () => {
        setIsShowBalanceDialogStorage(true)
        setOpenBalanceDialog(false)
    }

    const handleCloseDemoRegistrationDialog = () => {
        setOpenDemoRegistrationDialog(false)
    }

    const handleCloseCookiesDialog = () => {
        setOpenCookiesDialog(false)
        setIsShowCookiesDialog(true)
    }

    const handleClosePromoDialog = () => {
        setIsShowBalanceDialogStorage(true)
        setIsShowPromoDialogStorage(true)
        setOpenPromoDialog(false)
    }

    const handleCloseMeetCreateDialog = () => {
        setOpenDialogAddBotToMeetNow(false)
        setOpenDialogAddBotToMeet(false)
        setOpenDialogTranscriptVideo(false)
    }

    const handleCloseBalanceZeroDialog = () => {
        setOpenShowBalanceZeroDialog(false)
    }

    useEffect(() => {
        if (!isShowPromoDialogByFirstTime && isTempUser === false) {
            setOpenPromoDialog(true)
        }
    }, [isShowBalanceDialogByFirstTime, isNoneUserTariff, isTempUser])

    useEffect(() => {
        if (!isShowCookiesDialog) {
            setOpenCookiesDialog(true)
        }
    }, [isShowCookiesDialog])

    useEffect(() => {
        if (errorModelList && isShowPromoDialogByFirstTime) {
            setStateNotification({
                isOpen: true,
                text: errorModelList,
                isError: true,
                action: '/finances?isOpenTariffDialog=true',
            })
        }
    }, [errorModelList, isShowPromoDialogByFirstTime])

    useEffect(() => {
        clearRequest()
        clearRequestSetting()
        resetModelDetail()
    }, [pathname])

    useEffect(() => {
        if (11 > +balance && isUndefined(cookies.isShowBalanceDialogByUserBalance)) {
            setCookie('isShowBalanceDialogByUserBalance', true, {
                path: '/',
                expires: new Date(Date.now() + 86400000),
            })
            setOpenBalanceDialog(true)
        }
    }, [cookies, balance])

    useEffect(() => {
        if (isUndefined(balance) && balance <= 0 && isUndefined(cookies.isShowBalanceZeroDialog)) {
            setCookie('isShowBalanceZeroDialog', true, {
                path: '/',
                expires: dayjs(new Date()).add(1, 'hour').toDate(),
            })
            setOpenShowBalanceZeroDialog(true)
        }
    }, [balance, cookies])

    useEffect(() => {
        if (!userTariff?.is_active && userTariff) {
            setOpenBalanceDialog(true)
        }
    }, [userTariff])

    useEffect(() => {
        if (isDoneUpdateRequest) {
            setStateNotification({
                isOpen: true,
                text: errorUpdateRequest ? 'Возникла ошибка' : 'Данные успешно сохранены',
                isError: !!errorUpdateRequest,
            })
            setIsDoneUpdateRequest(false)
        }
    }, [isDoneUpdateRequest])

    useEffect(() => {
        if (user) {
            setIsTempUser(!!user?.profile?.is_temp)
        }
    }, [user])

    useEffect(() => {
        if (isTempUser && isShowRegistrationDialog) {
            setOpenDemoRegistrationDialog(true)
            setIsShowRegistrationDialog(false)
        }
    }, [isTempUser, isShowRegistrationDialog])

    useEffect(() => {
        const isHomePage = pathname === '/'
        if (
            isTempUser === false &&
            timeLeft === 0 &&
            !isUndefined(numberOfUnreadNotifications) &&
            isHomePage &&
            storageData &&
            projectListActive &&
            userTariff &&
            has(userPersonalData, 'banners')
        ) {
            const banners = userPersonalData.banners ?? {}
            const steps = Object.keys(banners)
                .filter((key) => key.startsWith('step_onboarding_') && banners[key] === true)
                .map((key) => parseInt(key.replace('step_onboarding_', '')))
                .filter((step) => !isNaN(step))

            const maxStep = steps.length > 0 ? Math.max(...steps) : 0
            setStepOnboarding(maxStep === 5 ? 100 : maxStep + 1)
        }
    }, [isTempUser, timeLeft, userPersonalData, numberOfUnreadNotifications, storageData, projectListActive, userTariff])

    useEffect(() => {
        const isHomePage = pathname === '/'
        let interval;
        if (isHomePage) {
           interval = setInterval(() => {
                setTimeLeft((prev) => {
                    if (prev <= 1) {
                        clearInterval(interval)
                        return 0
                    }
                    return prev - 1
                })
            }, 1000)
        }

        return () => clearInterval(interval)
    }, [])

    return (
        <WrapperNotification>
            <Grid
                container
                sx={{
                    display: 'flex',
                    height: isMobile ? '100dvh' : 'auto',
                }}>
                <HeaderApp />

                <Grid
                    container
                    spacing={isMobile ? 1 : 2}
                    sx={{
                        mt: hideHeader ? '0' : isMobile ? '44px': '80px',
                        p: isMobile ? 1 : 2,
                        pt: 0,
                        flexGrow: 1,
                    }}>
                    <NavigationSidebar />
                    <Grid
                        display="flex"
                        flexDirection="column"
                        gap="16px"
                        size="grow"
                        sx={{ ...styleChildren }}>
                        {children}
                    </Grid>

                    {openSidebarType === SidebarType.RECORD_SETTINGS &&
                        pathname.split('/')[1] === 'record' && <RecordsSetting />}

                    {openSidebarType === SidebarType.REQUESTS_SETTINGS &&
                        includes(
                            ['', 'request', 'requests', 'project'],
                            pathname.split('/')[1],
                        ) && <RequestsSetting />}
                    {openSidebarType === SidebarType.PROJECT_SETTINGS &&
                        includes(['project'], pathname.split('/')[1]) && <ProjectSettingsSidebar />}

                    {openSidebarType === SidebarType.ASSISTANT_SHOP &&
                        includes(['assistant-shop'], pathname.split('/')[1]) && (
                            <AssistantShopSidebar />
                        )}

                    {openSidebarType === SidebarType.CITATIONS &&
                        includes(
                            ['', 'request', 'requests', 'project'],
                            pathname.split('/')[1],
                        ) && <CitationSideBar />}

                    {openSidebarType === SidebarType.NOTIFICATIONS &&
                        !pathname.includes(NOTIFICATIONS_ROUTE) && <NotificationsSidebar />}

                    {openSidebarType === SidebarType.NOTIFICATIONS_SETTINGS && (
                        <NotificationsSettingsSidebar />
                    )}
                </Grid>
            </Grid>
            {/*<BalanceDialog open={openBalanceDialog} handleClose={handleCloseBalanceDialog} />*/}
            {/*<DiscountDialog />*/}
            {/* <PromoDialog open={openPromoDialog} handleClose={handleClosePromoDialog} /> */}
            <LoaderDialog open={isOpenLoader} />
            <DemoRegistrationDialog
                open={openDemoRegistrationDialog}
                handleClose={handleCloseDemoRegistrationDialog}
            />

            <CookiesDialog open={openCookiesDialog && stepOnboarding===100} handleClose={handleCloseCookiesDialog} />
            {(isOpenDialogAddBotToMeetNow || isOpenDialogAddBotToMeet) && (
                <MeetCreateDialog
                    open={isOpenDialogAddBotToMeetNow || isOpenDialogAddBotToMeet}
                    handleClose={handleCloseMeetCreateDialog}
                />
            )}
            <PackageSubscriptionUpdateDialog />
            {openRegularRequestsDialogThreadId && (
                <RegularRequestsDialog threadId={openRegularRequestsDialogThreadId} />
            )}

            {openShowBalanceZeroDialog && (
                <BalanceZeroDialog
                    open={openShowBalanceZeroDialog}
                    handleClose={handleCloseBalanceZeroDialog}
                />
            )}
            <Backdrop
                open={!isUndefined(stepOnboarding ) && stepOnboarding!== 100 && !isMobile}
                sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer }}
            />
            <OnboardingStep open={!isUndefined(stepOnboarding)  && stepOnboarding!== 100 && isMobile} />
        </WrapperNotification>
    )
}
